/**
 * useBankAutoAnalysisJob — hook do backend-driven auto-analizy Agenta Wyciągów.
 *
 * Zastępuje frontend loop (`useSequentialAgentRunner`) dla przycisku
 * „Auto: nieprzeanalizowani". Analiza działa po stronie backendu nawet
 * gdy user zamknie stronę — wystarczy że frontend ją wystartuje i pollerem
 * pobiera status.
 *
 * Lifecycle:
 *  1. mount → GET /auto-analysis-jobs/active — jeśli aktywny job istnieje,
 *     hook od razu przejmuje polling.
 *  2. start() → POST /start → trzyma job_id, włącza polling co 2s.
 *  3. polling co 2s → GET /{job_id}; gdy status terminalny → polling stop,
 *     onComplete callback.
 *  4. cancel() → POST /{job_id}/cancel — runner zauważy flag i przejdzie
 *     w `cancelled`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

// Polling: spokojny, niewchodzący w drogę usera. Pierwsze odświeżenie po
// 30s od startu (analiza dopiero się rozkręca), potem co 60s.
const INITIAL_POLL_DELAY_MS = 30_000;
const POLL_INTERVAL_MS = 60_000;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isActiveStatus = (s) => s === "queued" || s === "running";
const isTerminalStatus = (s) =>
  s === "completed" || s === "failed" || s === "cancelled";

export function useBankAutoAnalysisJob({ onComplete } = {}) {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [initialChecking, setInitialChecking] = useState(true);
  const pollTimerRef = useRef(null);
  const initialTimerRef = useRef(null);
  const lastTerminalIdRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (initialTimerRef.current) {
      clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
    }
  }, []);

  const handleTerminal = useCallback((finalJob) => {
    stopPolling();
    if (finalJob && lastTerminalIdRef.current !== finalJob.job_id) {
      lastTerminalIdRef.current = finalJob.job_id;
      try { onCompleteRef.current?.(finalJob); } catch (_) { /* ignore */ }
    }
  }, [stopPolling]);

  const pollOnce = useCallback(async (jobId) => {
    try {
      const r = await axios.get(
        `${API}/ai/agents/bank/auto-analysis-jobs/${jobId}`,
        { withCredentials: true }
      );
      const next = r.data?.job;
      if (next) {
        setJob(next);
        if (isTerminalStatus(next.status)) handleTerminal(next);
      }
    } catch (e) {
      // 404 = job nieistniejący → przestań pollować.
      if (e?.response?.status === 404) {
        stopPolling();
        setJob(null);
      }
    }
  }, [handleTerminal, stopPolling]);

  const startPolling = useCallback((jobId) => {
    stopPolling();
    // Pierwsze odświeżenie po INITIAL_POLL_DELAY_MS, potem regularnie co
    // POLL_INTERVAL_MS. Spokojny rytm — analiza trwa wiele sekund per item,
    // user nie potrzebuje sub-sekundowej widoczności.
    initialTimerRef.current = setTimeout(() => {
      pollOnce(jobId);
      pollTimerRef.current = setInterval(() => pollOnce(jobId), POLL_INTERVAL_MS);
    }, INITIAL_POLL_DELAY_MS);
  }, [pollOnce, stopPolling]);

  // Initial active job check (np. po powrocie na stronę).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/ai/agents/bank/auto-analysis-jobs/active`,
          { withCredentials: true }
        );
        if (!alive) return;
        const active = r.data?.job;
        if (active && isActiveStatus(active.status)) {
          setJob(active);
          startPolling(active.job_id);
        }
      } catch (_) { /* milcz */ } finally {
        if (alive) setInitialChecking(false);
      }
    })();
    return () => { alive = false; stopPolling(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = useCallback(async ({ forceReanalyze = false } = {}) => {
    if (starting) return null;
    if (job && isActiveStatus(job.status)) return job;
    setStarting(true);
    try {
      const r = await axios.post(
        `${API}/ai/agents/bank/auto-analysis-jobs/start`,
        { force_reanalyze: !!forceReanalyze },
        { withCredentials: true }
      );
      const newJob = r.data?.job;
      setJob(newJob);
      if (newJob && isActiveStatus(newJob.status)) {
        startPolling(newJob.job_id);
      } else if (newJob && isTerminalStatus(newJob.status)) {
        // Zero kandydatów → od razu terminal.
        handleTerminal(newJob);
      }
      return newJob;
    } finally {
      setStarting(false);
    }
  }, [starting, job, startPolling, handleTerminal]);

  const cancel = useCallback(async () => {
    if (!job?.job_id) return;
    try {
      await axios.post(
        `${API}/ai/agents/bank/auto-analysis-jobs/${job.job_id}/cancel`,
        {},
        { withCredentials: true }
      );
      // Polling przechwyci finalny status.
    } catch (_) { /* ignore */ }
  }, [job]);

  const running = !!job && isActiveStatus(job.status);
  const progressCurrent = job?.processed ?? 0;
  const progressTotal = job?.total ?? 0;
  const analyzed = job?.analyzed ?? 0;
  const cached = job?.cached ?? 0;
  const failed = job?.failed ?? 0;
  const currentName = job?.current_counterparty_key || "";
  const lastError = (job?.errors && job.errors.length > 0)
    ? job.errors[job.errors.length - 1]
    : null;

  return {
    job,
    running,
    starting,
    initialChecking,
    start,
    cancel,
    progress: { current: progressCurrent, total: progressTotal },
    analyzed,
    cached,
    failed,
    currentName,
    cancelRequested: !!job?.cancel_requested,
    lastUpdatedAt: job?.updated_at || null,
    lastError,
  };
}
